'use client';

import { useState, useRef, useEffect } from 'react';
import { Download, Copy, Check, Maximize2, Minimize2, Type, Bold, Italic, Heading1, Heading2, List, ListOrdered, Code, Image as ImageIcon, Link as LinkIcon, Quote, Minus, Table } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';
import { toast } from 'react-hot-toast';
import 'highlight.js/styles/github.css';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { markdownEditorContent } from '../../data/markdown-editor-content';
import Link from 'next/link';

type ViewMode = 'split' | 'editor' | 'preview';

export default function MarkdownEditor() {
  // State
  const [markdown, setMarkdown] = useState(`# Welcome to Markdown Editor

This is a **live** markdown editor with _preview_.

## Features

- 📝 Real-time preview
- ✨ Syntax highlighting
- 📋 Copy to clipboard
- 💾 Export to HTML/File
- ⌨️ Keyboard shortcuts

## Getting Started

1. Type markdown in the left panel
2. See the preview update in real-time
3. Use the toolbar for quick formatting

## Example Code Block

\`\`\`javascript
function helloWorld() {
  console.log('Hello, world!');
  return <div>React component</div>;
}
\`\`\`

## Table Example

| Syntax      | Description |
| ----------- | ----------- |
| Header      | Title       |
| Paragraph   | Text        |

---

> Made with ❤️ for your markdown needs`);

  const [viewMode, setViewMode] = useState<ViewMode>('split');
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  // Sample markdown content
  const samples = {
    basic: `# Heading 1
## Heading 2
### Heading 3

**Bold** and *italic* text

[Link](https://example.com)

![Image](https://via.placeholder.com/150)

- List item 1
- List item 2

1. First item
2. Second item

\`inline code\`

\`\`\`javascript
// Code block
function hello() {
  console.log('Hello!');
}
\`\`\``,
    table: `## Table Example

| Header 1 | Header 2 |
|----------|----------|
| Cell 1   | Cell 2   |
| Cell 3   | Cell 4   |

## Alignment

| Left-aligned | Center-aligned | Right-aligned |
|:-------------|:--------------:|--------------:|
| Left         | Center         | Right         |
| Text         | Text           | Text          |`,
    advanced: `## Advanced Features

### Task Lists

- [x] Write the press release
- [ ] Update the website
- [ ] Contact the media

### Strikethrough

~~This was a mistake~~

### Emoji

:smile: :rocket: :tada:

### HTML

<div style="color: red;">
  This is <strong>HTML</strong> inside markdown!
</div>

### Footnotes

Here's a simple footnote[^1].

[^1]: This is the footnote.`
  };

  // Insert text at cursor position
  const insertText = (prefix: string, suffix: string = '', offset: number = 0) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = markdown.substring(start, end);
    const before = markdown.substring(0, start);
    const after = markdown.substring(end);

    let newText, newCursorPos;

    if (start === end) {
      // No text selected, insert prefix and suffix with cursor in between
      newText = before + prefix + suffix + after;
      newCursorPos = start + prefix.length;
    } else {
      // Wrap selected text
      newText = before + prefix + selectedText + suffix + after;
      newCursorPos = end + prefix.length + suffix.length;
    }

    // Apply the change
    setMarkdown(newText);

    // Set cursor position after a small delay
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(
          newCursorPos + offset,
          newCursorPos + offset + (selectedText ? selectedText.length : 0)
        );
      }
    }, 10);
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Tab key
    if (e.key === 'Tab') {
      e.preventDefault();

      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      // If shift+tab, remove indentation
      if (e.shiftKey) {
        const before = markdown.substring(0, start);
        const after = markdown.substring(end);
        const lineStart = before.lastIndexOf('\n') + 1;
        const line = markdown.substring(lineStart, start);

        if (line.startsWith('  ')) {
          const newMarkdown = markdown.substring(0, lineStart) +
            line.substring(2) +
            markdown.substring(start);
          setMarkdown(newMarkdown);

          // Move cursor back 2 spaces
          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.selectionStart = Math.max(0, start - 2);
              textareaRef.current.selectionEnd = Math.max(0, end - 2);
            }
          }, 0);
        }
      } else {
        // Add 2 spaces for indentation
        insertText('  ');
      }
    }

    // Bold: Ctrl/Cmd + B
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
      e.preventDefault();
      insertText('**', '**');
    }

    // Italic: Ctrl/Cmd + I
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'i') {
      e.preventDefault();
      insertText('_', '_');
    }

    // Heading: Ctrl/Cmd + H
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'h') {
      e.preventDefault();
      insertText('## ', '');
    }
  };

  // Copy markdown to clipboard
  const copyToClipboard = () => {
    if (!markdown) return;

    navigator.clipboard.writeText(markdown).then(() => {
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }).catch((err) => {
      console.error('Failed to copy text: ', err);
      toast.error('Failed to copy to clipboard');
    });
  };

  // Export as HTML
  const exportAsHTML = () => {
    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Exported Markdown</title>
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.7.0/styles/github.min.css">
  <style>
    body {
      max-width: 800px;
      margin: 0 auto;
      padding: 2rem;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      line-height: 1.6;
      color: #24292e;
    }
    pre {
      background: #f6f8fa;
      padding: 1rem;
      border-radius: 6px;
      overflow-x: auto;
    }
    code {
      font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
      font-size: 85%;
    }
    table {
      border-collapse: collapse;
      width: 100%;
      margin: 1rem 0;
    }
    th, td {
      border: 1px solid #dfe2e5;
      padding: 0.5rem 1rem;
    }
    th {
      background-color: #f6f8fa;
    }
    blockquote {
      border-left: 4px solid #dfe2e5;
      padding-left: 1rem;
      color: #6a737d;
      margin: 1rem 0;
    }
  </style>
</head>
<body>
  <div class="markdown-body">
    ${markdown}
  </div>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'markdown-export.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Download as markdown file
  const downloadMarkdown = () => {
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'document.md';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Load sample markdown
  const loadSample = (sample: keyof typeof samples) => {
    setMarkdown(samples[sample]);
    toast.success('Sample loaded!');
  };

  // Clear editor
  const clearEditor = () => {
    if (confirm('Are you sure you want to clear the editor? This cannot be undone.')) {
      setMarkdown('');
      toast.success('Editor cleared');
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <div className="mb-6">
        <ToolPageIntro title="Markdown Editor">
                Write and preview Markdown in real-time with our free online Markdown editor. Perfect for documentation, README files, and content creation.
            </ToolPageIntro>
      </div>

      <div className="flex flex-col space-y-4 mb-6">
        <div className="flex flex-wrap gap-2">
          <div className="flex-1 flex space-x-2 overflow-x-auto pb-2">
            <button
              onClick={() => insertText('**', '**')}
              className="p-2 rounded hover:bg-gray-100"
              title="Bold (Ctrl+B)"
            >
              <Bold className="h-5 w-5" />
            </button>
            <button
              onClick={() => insertText('_', '_')}
              className="p-2 rounded hover:bg-gray-100"
              title="Italic (Ctrl+I)"
            >
              <Italic className="h-5 w-5" />
            </button>
            <button
              onClick={() => insertText('# ', '')}
              className="p-2 rounded hover:bg-gray-100"
              title="Heading 1"
            >
              <Heading1 className="h-5 w-5" />
            </button>
            <button
              onClick={() => insertText('## ', '')}
              className="p-2 rounded hover:bg-gray-100"
              title="Heading 2"
            >
              <Heading2 className="h-5 w-5" />
            </button>
            <button
              onClick={() => insertText('- ', '')}
              className="p-2 rounded hover:bg-gray-100"
              title="Bullet List"
            >
              <List className="h-5 w-5" />
            </button>
            <button
              onClick={() => insertText('1. ', '')}
              className="p-2 rounded hover:bg-gray-100"
              title="Numbered List"
            >
              <ListOrdered className="h-5 w-5" />
            </button>
            <button
              onClick={() => insertText('```\n', '\n```', 4)}
              className="p-2 rounded hover:bg-gray-100"
              title="Code Block"
            >
              <Code className="h-5 w-5" />
            </button>
            <button
              onClick={() => insertText('`', '`')}
              className="p-2 rounded hover:bg-gray-100"
              title="Inline Code"
            >
              <Type className="h-5 w-5" />
            </button>
            <button
              onClick={() => insertText('[', '](https://)')}
              className="p-2 rounded hover:bg-gray-100"
              title="Link"
            >
              <LinkIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => insertText('![', '](https://via.placeholder.com/150)')}
              className="p-2 rounded hover:bg-gray-100"
              title="Image"
            >
              <ImageIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => insertText('> ', '')}
              className="p-2 rounded hover:bg-gray-100"
              title="Blockquote"
            >
              <Quote className="h-5 w-5" />
            </button>
            <button
              onClick={() => insertText('---\n', '')}
              className="p-2 rounded hover:bg-gray-100"
              title="Horizontal Rule"
            >
              <Minus className="h-5 w-5" />
            </button>
            <button
              onClick={() => insertText('| Column 1 | Column 2 |\n|----------|----------|\n| Cell 1   | Cell 2   |\n| Cell 3   | Cell 4   |\n', '')}
              className="p-2 rounded hover:bg-gray-100"
              title="Table"
            >
              <Table className="h-5 w-5" />
            </button>
          </div>

          <div className="flex space-x-2">
            <select
              onChange={(e) => loadSample(e.target.value as keyof typeof samples)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white"
              title="Load Sample"
            >
              <option value="">Samples</option>
              <option value="basic">Basic Markdown</option>
              <option value="table">Table Example</option>
              <option value="advanced">Advanced Features</option>
            </select>

            <button
              onClick={clearEditor}
              className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-md border border-red-200"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between bg-gray-50 p-2 rounded-t-lg border border-b-0 border-gray-200">
          <div className="flex space-x-1">
            <button
              onClick={() => setViewMode('editor')}
              className={`px-3 py-1 text-sm rounded-md ${viewMode === 'editor' ? 'bg-white shadow-sm border border-gray-300' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              Editor
            </button>
            <button
              onClick={() => setViewMode('split')}
              className={`px-3 py-1 text-sm rounded-md ${viewMode === 'split' ? 'bg-white shadow-sm border border-gray-300' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              Split View
            </button>
            <button
              onClick={() => setViewMode('preview')}
              className={`px-3 py-1 text-sm rounded-md ${viewMode === 'preview' ? 'bg-white shadow-sm border border-gray-300' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              Preview
            </button>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={copyToClipboard}
              className="flex items-center px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {copied ? (
                <Check className="h-4 w-4 mr-1 text-green-500" />
              ) : (
                <Copy className="h-4 w-4 mr-1" />
              )}
              {copied ? 'Copied!' : 'Copy'}
            </button>

            <button
              onClick={downloadMarkdown}
              className="flex items-center px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <Download className="h-4 w-4 mr-1" />
              .md
            </button>

            <button
              onClick={exportAsHTML}
              className="flex items-center px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <Download className="h-4 w-4 mr-1" />
              .html
            </button>

            <button
              onClick={() => {
                if (viewMode === 'split') {
                  const editor = document.querySelector('.editor-pane') as HTMLElement;
                  const preview = document.querySelector('.preview-pane') as HTMLElement;

                  if (editor && preview) {
                    if (editor.style.flex === '1') {
                      editor.style.flex = '2';
                      preview.style.flex = '1';
                    } else {
                      editor.style.flex = '1';
                      preview.style.flex = '2';
                    }
                  }
                }
              }}
              className="p-1 text-gray-500 hover:text-gray-700"
              title="Toggle pane sizes"
            >
              {viewMode === 'split' ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      <div className={`flex flex-col md:flex-row ${viewMode === 'split' ? 'h-[calc(100vh-250px)]' : 'h-[calc(100vh-200px)]'} bg-white border border-gray-200 rounded-b-lg rounded-tr-lg overflow-hidden`}>
        {(viewMode === 'editor' || viewMode === 'split') && (
          <div
            className={`editor-pane ${viewMode === 'split' ? 'md:w-1/2 border-r border-gray-200' : 'w-full'}`}
            style={{ flex: viewMode === 'split' ? '1' : '1' }}
          >
            <textarea
              ref={textareaRef}
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full h-full p-4 font-mono text-sm text-gray-800 focus:outline-none resize-none"
              placeholder="Start writing your markdown here..."
              spellCheck="false"
            />
          </div>
        )}

        {(viewMode === 'preview' || viewMode === 'split') && (
          <div
            ref={previewRef}
            className={`preview-pane overflow-y-auto p-4 ${viewMode === 'split' ? 'md:w-1/2' : 'w-full'} bg-white`}
            style={{ flex: viewMode === 'split' ? '1' : '1' }}
          >
            {markdown ? (
              <div className="prose prose-sm sm:prose lg:prose-lg max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeHighlight]}
                  components={{
                    // Customize the rendering of links to open in a new tab
                    a: ({ node, ...props }) => (
                      <a
                        {...props}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      />
                    ),
                    // Customize code blocks
                    pre: ({ node, ...props }) => (
                      <div className="relative">
                        <pre {...props} className="rounded-lg overflow-x-auto" />
                      </div>
                    ),
                    // Customize inline code
                    code: ({ node, className, children, ...props }: any) => {
                      const match = /language-(\w+)/.exec(className || '');
                      const isInline = !className;
                      return !isInline && match ? (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      ) : (
                        <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono text-red-600" {...props}>
                          {children}
                        </code>
                      );
                    },
                    // Customize blockquotes
                    blockquote: ({ node, ...props }) => (
                      <blockquote
                        className="border-l-4 border-gray-300 pl-4 text-gray-600 italic"
                        {...props}
                      />
                    ),
                    // Customize tables
                    table: ({ node, ...props }) => (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200" {...props} />
                      </div>
                    ),
                    th: ({ node, ...props }) => (
                      <th
                        className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        {...props}
                      />
                    ),
                    td: ({ node, ...props }) => (
                      <td
                        className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 border-b border-gray-200"
                        {...props}
                      />
                    ),
                  }}
                >
                  {markdown}
                </ReactMarkdown>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <p>Preview will appear here</p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-6 bg-blue-50 p-4 rounded-lg border border-blue-100">
        <h3 className="font-medium text-blue-800 mb-2">Markdown Cheat Sheet</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <h4 className="font-medium text-blue-700 text-sm mb-1">Text</h4>
            <table className="w-full text-sm text-gray-700">
              <tbody>
                <tr>
                  <td className="py-1"><code>**bold**</code></td>
                  <td className="py-1"><strong>bold</strong></td>
                </tr>
                <tr>
                  <td className="py-1"><code>*italic*</code></td>
                  <td className="py-1"><em>italic</em></td>
                </tr>
                <tr>
                  <td className="py-1"><code>~~strikethrough~~</code></td>
                  <td className="py-1"><s>strikethrough</s></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="font-medium text-blue-700 text-sm mb-1">Headers</h4>
            <table className="w-full text-sm text-gray-700">
              <tbody>
                <tr>
                  <td className="py-1"><code># H1</code></td>
                  <td className="py-1 text-xl font-bold">H1</td>
                </tr>
                <tr>
                  <td className="py-1"><code>## H2</code></td>
                  <td className="py-1 text-lg font-bold">H2</td>
                </tr>
                <tr>
                  <td className="py-1"><code>### H3</code></td>
                  <td className="py-1 text-base font-bold">H3</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="font-medium text-blue-700 text-sm mb-1">Lists</h4>
            <table className="w-full text-sm text-gray-700">
              <tbody>
                <tr>
                  <td className="py-1"><code>- Item 1</code></td>
                  <td className="py-1">• Item 1</td>
                </tr>
                <tr>
                  <td className="py-1"><code>1. Item 1</code></td>
                  <td className="py-1">1. Item 1</td>
                </tr>
                <tr>
                  <td className="py-1"><code>[ ] Task</code></td>
                  <td className="py-1">
                    <input type="checkbox" className="mr-1" /> Task
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="font-medium text-blue-700 text-sm mb-1">Other</h4>
            <table className="w-full text-sm text-gray-700">
              <tbody>
                <tr>
                  <td className="py-1"><code>[Link](url)</code></td>
                  <td className="py-1"><a href="#" className="text-blue-600">Link</a></td>
                </tr>
                <tr>
                  <td className="py-1"><code>![Alt](src)</code></td>
                  <td className="py-1">Image</td>
                </tr>
                <tr>
                  <td className="py-1"><code>```code```</code></td>
                  <td className="py-1">Code block</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Informational Content */}
      <div className="mt-12 bg-white rounded-lg shadow-lg p-8">
        <InformationalContent
          title={markdownEditorContent.title}
          description={markdownEditorContent.description}
          content={markdownEditorContent.content}
          faqs={markdownEditorContent.faqs}
          relatedTools={markdownEditorContent.relatedTools}
        />
      </div>
    </div>
  );
}
